"use client";

import { useEffect, useState } from "react";
import { Session } from "next-auth";
import { signIn } from "next-auth/react";
import Link from "next/link";

import { VscSparkle } from "react-icons/vsc";
import { SiGithubsponsors } from "react-icons/si";
import { LuHome, LuSettings } from "react-icons/lu";
import { RiCoinLine, RiLogoutBoxLine } from "react-icons/ri";

import { api } from "~/utils/api";
import { useLogout } from "~/hooks/useLogout";
import { useHomePageRenderStore, useUserStore } from "~/state/store";

import RiverLogoWhite from "../themed/Logo";
import { If } from "../Conditional";
import { Button } from "../themed/Button";
import { UserAvatar } from "~/components/v4/shadcn/UserAvatar";
import { slugifyUser } from "~/utils/string";

type Props = {
    sessionData: Session | null;
    closeMobileSheet: () => void;
};

export const MobileLoggenInDescription = ({
    sessionData,
    closeMobileSheet,
}: Props) => {
    const { data: fullUser, isLoading } = api.user.getUserById.useQuery(
        {
            userId: sessionData?.user?.id ?? "",
        },
        {
            enabled: !!sessionData?.user?.id,
        },
    );

    const { setTab } = useHomePageRenderStore();
    const handleLogout = useLogout();
    const { user, setUser } = useUserStore((state) => ({
        user: state.user,
        setUser: state.setUser,
    }));

    const [editProfile, showEditProfile] = useState(false);

    useEffect(() => {
        const setUserStoreUser = async () => {
            if (sessionData?.user?.id && user === null) {
                if (!fullUser) return;
                else setUser(fullUser);
            }
        };
        setUserStoreUser();

        if (!isLoading && (!fullUser?.name || !fullUser?.bio)) {
            showEditProfile(true);
        }
    }, [sessionData?.user, setUser, isLoading, fullUser]);

    return (
        <div className="flex h-full min-h-[45vh] flex-col text-white">
            <div className="flex flex-col gap-4">
                {sessionData?.user && <UserAvatar user={sessionData.user} />}
                <Link
                    href={`/profile/${slugifyUser({
                        name: sessionData?.user?.name,
                        id: sessionData?.user?.id,
                        slug: sessionData?.user?.slug,
                    })}`}
                    onClick={closeMobileSheet}
                    className="flex w-full flex-col gap-1 text-sm"
                >
                    <span>{sessionData?.user?.name}</span>
                    <div className="flex items-center gap-2 text-xs tracking-wide text-[#A3A8B4] hover:underline">
                        {editProfile ? (
                            <Link
                                className="text-secondary"
                                href={`/profile/${slugifyUser({
                                    name: sessionData?.user?.name,
                                    id: sessionData?.user?.id,
                                    slug: sessionData?.user?.slug,
                                })}?editMode=true`}
                            >
                                Update Profile
                            </Link>
                        ) : (
                            <Link
                                href={`/profile/${slugifyUser({
                                    name: sessionData?.user?.name,
                                    id: sessionData?.user?.id,
                                    slug: sessionData?.user?.slug,
                                })}`}
                            >
                                View my profile
                            </Link>
                        )}
                    </div>
                </Link>
            </div>

            <div className="mt-6 flex flex-col gap-7">
                <div
                    onClick={() => {
                        setTab("events");
                        closeMobileSheet();
                    }}
                >
                    <Link href="/home" className="flex items-center gap-2">
                        <LuHome className="h-4 w-4" />
                        <span>Home</span>
                    </Link>
                </div>
                <Link
                    href="/discovery/communities"
                    onClick={closeMobileSheet}
                    className="flex items-center gap-2"
                >
                    <VscSparkle className="h-5 w-5" />
                    <span>Discover</span>
                </Link>
                <Link
                    href={`/profile/${slugifyUser({
                        name: sessionData?.user?.name,
                        id: sessionData?.user?.id,
                        slug: sessionData?.user?.slug,
                    })}?edit=true`}
                    onClick={closeMobileSheet}
                    className="flex items-center gap-2"
                >
                    <LuSettings className="h-4 w-4" />
                    Settings
                </Link>
                <If
                    condition={
                        !!fullUser?.reimbursementRequests &&
                        fullUser.reimbursementRequests.length > 0
                    }
                >
                    <Link
                        href="/reimbursement-requests/list"
                        onClick={closeMobileSheet}
                        className="flex items-center gap-2"
                    >
                        <RiCoinLine className="h-4 w-4" />
                        <span>Reimbursements</span>
                    </Link>
                </If>
                <If
                    condition={
                        !!fullUser?.sponsorApplications &&
                        fullUser?.sponsorApplications.length > 0
                    }
                >
                    <Link
                        href="/sponsor-applications/list"
                        onClick={closeMobileSheet}
                        className="flex items-center gap-2"
                    >
                        <SiGithubsponsors className="h-4 w-4" />
                        <span>Sponsor applications</span>
                    </Link>
                </If>

                <div className="flex items-center gap-2" onClick={handleLogout}>
                    <RiLogoutBoxLine className="h-4 w-4" />
                    <span>Log out</span>
                </div>
            </div>
        </div>
    );
};

export const MobileLoggenInFooter = ({
    sessionData,
    closeMobileSheet,
}: Props) => {
    return (
        <div className="flex flex-col gap-4 border-t-[1px] border-v3-N-500 py-6">
            <Link href="https://www.getriver.io" onClick={closeMobileSheet}>
                About River
            </Link>
            <Link
                key="pricing"
                href="https://www.getriver.io/pricing"
                className="transition-colors hover:text-white/80"
                target="_blank"
                onClick={closeMobileSheet}
            >
                Pricing
            </Link>
            <Link
                key="help"
                href="https://intercom.help/rvr/en"
                className="hidden transition-colors hover:text-white/80 md:block"
                target="_blank"
                onClick={closeMobileSheet}
            >
                Help Center
            </Link>
            <Link
                key="newsletter"
                href="https://thecurrentbyriver.substack.com/"
                className="transition-colors hover:text-white/80"
                target="_blank"
                onClick={closeMobileSheet}
            >
                Newsletter
            </Link>

            {sessionData?.user.role === "ADMIN" && (
                <>
                    <Link href="/admin">Admin</Link>
                </>
            )}
        </div>
    );
};

export const MobileLoggenOutDescription = ({
    closeMobileSheet,
}: {
    closeMobileSheet: () => void;
}) => {
    return (
        <div className="flex h-full min-h-[45vh] flex-col text-lg text-white">
            <div className="py-6">
                <RiverLogoWhite />
            </div>

            <div className="mt-6 flex flex-col gap-5">
                <Link href="/home" onClick={closeMobileSheet}>
                    <span>Home</span>
                </Link>
                <Link
                    href="/discovery/communities"
                    onClick={closeMobileSheet}
                    className="underline"
                >
                    Communities
                </Link>
                <Link href="https://www.getriver.io">About River</Link>
            </div>
        </div>
    );
};

export const MobileLoggenOutFooter = () => {
    return (
        <div className="flex flex-col gap-4 border-t-[1px] border-v3-N-500 py-6">
            <div className="text-[30px]">
                <div>Meet like-minded </div>
                <div>people in your city.</div>
            </div>

            <div className="flex gap-3">
                <Button
                    onClick={() => {
                        signIn().catch(console.debug);
                    }}
                >
                    Sign Up
                </Button>

                <Button
                    onClick={() => {
                        signIn().catch(console.debug);
                    }}
                    variant="link"
                >
                    Log in
                </Button>
            </div>
        </div>
    );
};
